import { ViewStyle, TextStyle, StyleSheet, Pressable} from 'react-native';
import React, {useState} from 'react';
import {Icon, Text} from 'ui-kits';
import {SearchDropDown} from './MultiSelect/SearchDropDown';
import {spacing, corner_radius} from 'theme/Units';
import colors from 'theme/Colors';

type sizeTypes = 'lg' | 'md' | 'sm';

type propType = Readonly<{
  placeholder?: string;
  size?: sizeTypes;
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;
  searchPlaceholder?: string;
  selectedEntries?: any[];
  multiple?: boolean;
  options: any[];
  onSave: (selectedOptions: any[]) => void;
  keyExtractor: (item: any) => string;
  searchKeyExtractor: (item: any) => string;
  renderItem: (item: any) => JSX.Element;
  displayItem?: (item: any) => JSX.Element;
  style?: ViewStyle;
}>;

export function SearchSelect(props: propType) {
  const {
    placeholder,
    size = 'md',
    leftIcon = null,
    rightIcon = null,
    keyExtractor,
    onSave,
    options,
    renderItem,
    searchKeyExtractor,
    searchPlaceholder = '',
    selectedEntries = [],
    style: overrideStyle,
    displayItem,
    multiple = true,
  } = props;

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<any[]>(
    selectedEntries || [],
  );

  const left_icon = leftIcon || <Icon type={'search'} size={14} />;

  const right_icon = rightIcon || <Icon type={'cheveron_down'} size={14} />;

  function handleSave(savedOptions: any[]) {
    setSelectedOptions(savedOptions);
    onSave?.(savedOptions);
    setDropdownOpen(false);
  }

  // this variable will have the string to be display when we have some selected option/values
  const valueIndicator = multiple
    ? `${selectedOptions.length} selected`
    : displayItem?.(selectedOptions[0]);

  return (
    <>
      <Pressable
        onPress={() => setDropdownOpen(!dropdownOpen)}
        style={[styles.inputWrapper, sizeStyles[size], overrideStyle]}>
        {left_icon}

        <Text style={styles.placeholder}>
          {selectedOptions.length > 0 ? valueIndicator : placeholder}
        </Text>

        {right_icon}
      </Pressable>

      {dropdownOpen ? (
        <SearchDropDown
          onClose={() => setDropdownOpen(false)}
          onSave={handleSave}
          optionKeyExtractor={keyExtractor}
          options={options}
          renderOption={renderItem}
          searchKeyExtractor={searchKeyExtractor}
          searchPlaceholder={searchPlaceholder}
          singleSelect={!multiple}
          selectedEntries={selectedEntries}
          mode="popup"
          style={styles.searchDropdown}
        />
      ) : null}
    </>
  );
}

type styleTypes = {
  inputWrapper: ViewStyle;
  inputField: TextStyle;
  searchDropdownWrapper: ViewStyle;
  searchDropdown: ViewStyle;
  placeholder: TextStyle;
};

const styles: styleTypes = StyleSheet.create({
  searchDropdown: {
    top: 0,
    height: 400,
  },
  placeholder: {
    color: colors.palette.nuetral4,
    flex: 1,
    marginLeft: 8,
    fontWeight: '500',
  },
  searchDropdownWrapper: {
    position: 'absolute',
    width: '100%',
    bottom: 0,
    left: 0,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing['2'],
    borderRadius: corner_radius.rounded,
    backgroundColor: colors.palette.white,
    borderWidth: 1,
    gap: spacing['2'],
    borderColor: colors.palette.stroke,
    width: '100%',
  },
  inputField: {
    flex: 1,
    fontWeight: '500',
    color: colors.palette.nuetral4,
    backgroundColor: 'transparent',
  },
});

const sizeStyles: Record<sizeTypes, object> = {
  lg: {height: 48, fontSize: 18},
  md: {height: 40, fontSize: 14},
  sm: {height: 32, fontSize: 14},
};
